const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const realtimeBaseUrl = () => {
  // Cookie auth is host-scoped. When frontend and backend use different preview
  // domains, connecting directly to BACKEND_URL drops the frontend login cookie.
  // Use the same-origin nginx /api proxy so WebSocket auth sees the same cookie.
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return BACKEND_URL;
};

const toWsUrl = (baseUrl) => {
  if (!baseUrl) return "";
  if (baseUrl.startsWith("https://")) return baseUrl.replace("https://", "wss://");
  if (baseUrl.startsWith("http://")) return baseUrl.replace("http://", "ws://");
  return baseUrl;
};

export const connectWS = (_token, onMessage) => {
  const wsBase = toWsUrl(realtimeBaseUrl());
  let socket = null;
  let reconnectTimer = null;
  let stableConnectionTimer = null;
  let reconnectAttempts = 0;
  let closedByClient = false;

  const scheduleReconnect = () => {
    if (closedByClient || reconnectAttempts >= 3) return;
    reconnectAttempts += 1;
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      void openSocket();
    }, 1500 * reconnectAttempts);
  };

  const fetchRealtimeTicket = async () => {
    const response = await fetch("/api/realtime/ticket", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Realtime ticket unavailable");
    const payload = await response.json();
    if (!payload?.ticket) throw new Error("Realtime ticket missing");
    return payload.ticket;
  };

  const openSocket = async () => {
    if (closedByClient) return;
    try {
      const ticket = await fetchRealtimeTicket();
      if (closedByClient) return;
      socket = new WebSocket(`${wsBase}/api/ws/events`, ["aion-realtime-v1", `aion-ticket.${ticket}`]);
    } catch {
      scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      if (stableConnectionTimer) window.clearTimeout(stableConnectionTimer);
      stableConnectionTimer = window.setTimeout(() => {
        reconnectAttempts = 0;
        stableConnectionTimer = null;
      }, 30000);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch {
        // Ignore malformed realtime frames without tearing down the session.
      }
    };

    socket.onerror = () => {
      // onclose owns reconnect scheduling.
    };

    socket.onclose = () => {
      if (stableConnectionTimer) {
        window.clearTimeout(stableConnectionTimer);
        stableConnectionTimer = null;
      }
      scheduleReconnect();
    };
  };

  void openSocket();

  return {
    close() {
      closedByClient = true;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (stableConnectionTimer) {
        window.clearTimeout(stableConnectionTimer);
        stableConnectionTimer = null;
      }
      if (socket) {
        socket.onclose = null;
        try {
          socket.close();
        } catch {
          // Ignore close races.
        }
      }
    },
  };
};
